import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import { assetSchema, type A2vAsset } from '../assets/model';

export enum A2vTemplateType {
  Image = 'IMAGE',
  Slideshow = 'SLIDESHOW',
  Video = 'VIDEO',
}

export enum A2vOverlayPosition {
  TopLeft = 'TOP_LEFT',
  TopRight = 'TOP_RIGHT',
  BottomLeft = 'BOTTOM_LEFT',
  BottomRight = 'BOTTOM_RIGHT',
}

export enum A2vAssetSelectionKind {
  Logo = 'LOGO',
  Images = 'IMAGES',
  Video = 'VIDEO',
}

export interface A2vConfiguration {
  templateType: A2vTemplateType;
  qrCodeEnabled: boolean;
  qrCodeLink?: string | undefined;
  qrCodePosition: A2vOverlayPosition;
  logoEnabled: boolean;
  logo?: A2vAsset | undefined;
  logoPosition: A2vOverlayPosition;
  images: A2vAsset[];
  video?: A2vAsset | undefined;
  [key: string]: unknown;
}

export interface NetworkTemplateConfiguration {
  a2vConfiguration: A2vConfiguration;
  network?: unknown;
  publisher?: unknown;
  useCustomTemplate?: boolean;
  [key: string]: unknown;
}

const networkTemplateAssetSchema = assetSchema.loose();

const rawA2vConfigurationSchema = z.looseObject({
  templateType: nullableOptional(z.enum(A2vTemplateType)),
  qrCodeEnabled: nullableOptional(z.boolean()),
  qrCodeLink: nullableOptional(z.string()),
  qrCodePosition: nullableOptional(z.enum(A2vOverlayPosition)),
  logoEnabled: nullableOptional(z.boolean()),
  logo: nullableOptional(networkTemplateAssetSchema),
  logoPosition: nullableOptional(z.enum(A2vOverlayPosition)),
  images: nullableArray(networkTemplateAssetSchema),
  video: nullableOptional(networkTemplateAssetSchema),
});

const a2vConfigurationSchema = rawA2vConfigurationSchema.transform(
  (configuration): A2vConfiguration => {
    const defaults = createDefaultA2vConfiguration();
    return {
      ...configuration,
      templateType: configuration.templateType ?? defaults.templateType,
      qrCodeEnabled: configuration.qrCodeEnabled ?? defaults.qrCodeEnabled,
      qrCodeLink: configuration.qrCodeLink,
      qrCodePosition: configuration.qrCodePosition ?? defaults.qrCodePosition,
      logoEnabled: configuration.logoEnabled ?? defaults.logoEnabled,
      logo: configuration.logo,
      logoPosition: configuration.logoPosition ?? defaults.logoPosition,
      images: configuration.images,
      video: configuration.video,
    };
  },
);

export const networkTemplateConfigurationSchema = z
  .preprocess(
    (response) =>
      response === null || (typeof response === 'string' && response.trim() === '') ? {} : response,
    z.looseObject({
      a2vConfiguration: nullableOptional(a2vConfigurationSchema),
      network: z.unknown().optional(),
      publisher: z.unknown().optional(),
      useCustomTemplate: z.boolean().optional().default(false),
    }),
  )
  .transform((configuration): NetworkTemplateConfiguration => ({
    ...configuration,
    a2vConfiguration:
      configuration.a2vConfiguration === undefined
        ? createDefaultA2vConfiguration()
        : configuration.a2vConfiguration,
  }));

const wrappedA2vConfigurationSchema = z.looseObject({
  a2vConfiguration: a2vConfigurationSchema,
});

export const networkTemplateUpdateResponseSchema = z
  .preprocess(
    (response) => (typeof response === 'string' && response.trim() === '' ? null : response),
    z.union([wrappedA2vConfigurationSchema, a2vConfigurationSchema, z.null()]),
  )
  .transform((response): A2vConfiguration | null => {
    if (response === null) return null;
    const wrapped = wrappedA2vConfigurationSchema.safeParse(response);
    return wrapped.success ? wrapped.data.a2vConfiguration : a2vConfigurationSchema.parse(response);
  });

export const qrCodeAssetSchema = z.string();

export const livePreviewResponseSchema = z.array(
  z.looseObject({
    convertedVideos: z.array(
      z.looseObject({
        videoPath: z.string(),
        width: z.number().optional(),
        height: z.number().optional(),
        bitrate: z.number().optional(),
      }),
    ),
  }),
);

export type A2vLivePreviewResponse = z.infer<typeof livePreviewResponseSchema>;

export interface A2vLivePreviewRequest {
  configuration: {
    type: A2vTemplateType;
    qrCodeAsset: string | null;
    qrCodePosition: A2vOverlayPosition | null;
    logoAsset: string | null;
    logoPosition: A2vOverlayPosition | null;
    imageAssets: string[] | null;
    videoAsset: string | null;
  };
  audioFiles: Array<{
    url: string;
    durationSec: number;
  }>;
}

export const a2vOverlayPositions = Object.values(A2vOverlayPosition);

export function createDefaultA2vConfiguration(): A2vConfiguration {
  return {
    templateType: A2vTemplateType.Image,
    qrCodeEnabled: false,
    qrCodePosition: A2vOverlayPosition.BottomRight,
    logoEnabled: false,
    logoPosition: A2vOverlayPosition.TopLeft,
    images: [],
  };
}

export function createLivePreviewRequest(
  configuration: A2vConfiguration,
  qrCodeAsset: string | null,
  sampleAudioUrl: string,
): A2vLivePreviewRequest {
  const videoTemplate = configuration.templateType === A2vTemplateType.Video;
  return {
    configuration: {
      type: configuration.templateType,
      qrCodeAsset: configuration.qrCodeEnabled ? qrCodeAsset : null,
      qrCodePosition: configuration.qrCodeEnabled ? configuration.qrCodePosition : null,
      logoAsset: configuration.logoEnabled ? (configuration.logo?.uri ?? null) : null,
      logoPosition: configuration.logoEnabled ? configuration.logoPosition : null,
      imageAssets: videoTemplate
        ? null
        : configuration.images.map((asset) => asset.uri).filter(isNonEmptyString),
      videoAsset: videoTemplate ? (configuration.video?.uri ?? null) : null,
    },
    audioFiles: [{ url: sampleAudioUrl, durationSec: 21 }],
  };
}

export function configurationHasTemplateAssets(configuration: A2vConfiguration) {
  return configuration.templateType === A2vTemplateType.Video
    ? Boolean(configuration.video)
    : configuration.images.length > 0;
}

export function configurationHasValidOverlays(
  configuration: A2vConfiguration,
  qrCodeAsset: string | null,
) {
  if (configuration.logoEnabled && !configuration.logo) return false;
  if (configuration.qrCodeEnabled && (!isValidHttpUrl(configuration.qrCodeLink) || !qrCodeAsset)) {
    return false;
  }
  return !(
    configuration.logoEnabled &&
    configuration.qrCodeEnabled &&
    configuration.logoPosition === configuration.qrCodePosition
  );
}

export function nextAvailableOverlayPosition(
  occupied: A2vOverlayPosition | undefined,
  preferred: A2vOverlayPosition,
) {
  return occupied === preferred
    ? (a2vOverlayPositions.find((position) => position !== occupied) ?? preferred)
    : preferred;
}

export function isValidHttpUrl(value: string | undefined) {
  if (!value?.trim()) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isNonEmptyString(value: string | undefined): value is string {
  return Boolean(value?.trim());
}
